/* eslint-disable no-autofix/jsdoc/require-jsdoc */
import { useQuery } from '@tanstack/react-query';

import type { PharmacistOrderTableView } from '@/context/orders/PharmacistOrders';
import { getPharmacistOrders } from '@/services/pharmacist.service';
import type { OrderStatus, TableSortOrder } from '@montugroup/circuit-shared/types/index';

export type UseGetPharmacistOrdersParams = {
  page: number;
  pageSize: number;
  sortingOrder: TableSortOrder;
  filter: string;
  activeTab: PharmacistOrderTableView;
  statusFilter: OrderStatus[];
  startDate: Date | null;
  endDate: Date | null;
  isEnabled: boolean;
};

function useGetPharmacistOrders({
  page,
  pageSize,
  sortingOrder,
  filter,
  activeTab,
  statusFilter,
  isEnabled,
  startDate,
  endDate,
}: UseGetPharmacistOrdersParams) {
  const { isLoading, data, isError, isSuccess, refetch } = useQuery({
    queryKey: [
      'getPharmacistOrders',
      page,
      pageSize,
      sortingOrder,
      filter,
      activeTab,
      statusFilter,
      startDate?.toDateString(),
      endDate?.toDateString(),
    ],
    queryFn: async ({ signal }) => {
      try {
        const response = await getPharmacistOrders(
          pageSize,
          page,
          sortingOrder,
          filter,
          activeTab,
          statusFilter,
          startDate,
          endDate,
          signal,
        );
        if (response.status !== 200) {
          throw new Error(`Request failed with status code ${response.status}`);
        }
        return response.data;
      } catch (error) {
        throw new Error(`Failed to fetch pharmacist orders: ${(error as { message: string }).message}`);
      }
    },
    enabled: isEnabled,
  });

  return { isLoading, data, isError, isSuccess, refetch };
}

export default useGetPharmacistOrders;
